/**
 * Signal metrics for KT1 captures. They describe the audio the browser
 * actually delivered, so capture strategies can be compared on a real device
 * and FR-74 `audio_quality` thresholds can later be calibrated from data.
 */

export interface SignalStats {
  frames: number
  durationSeconds: number
  /** Largest absolute sample, 0–1. */
  peak: number
  /** Whole-signal RMS in dBFS; null for digital silence. */
  rmsDbfs: number | null
  /** Samples at or beyond full scale. */
  clippedSamples: number
  dcOffset: number
  /** Share of 20 ms windows quieter than -50 dBFS. */
  silentRatio: number
}

const CLIP_THRESHOLD = 0.999
const SILENCE_DBFS = -50
const WINDOW_SECONDS = 0.02

export function analyseSignal(samples: Float32Array, sampleRate: number): SignalStats {
  let peak = 0
  let sumSquares = 0
  let sum = 0
  let clipped = 0

  for (const s of samples) {
    const a = Math.abs(s)
    if (a > peak) peak = a
    if (a >= CLIP_THRESHOLD) clipped++
    sumSquares += s * s
    sum += s
  }

  const n = samples.length
  const rms = n > 0 ? Math.sqrt(sumSquares / n) : 0

  return {
    frames: n,
    durationSeconds: sampleRate > 0 ? n / sampleRate : 0,
    peak,
    rmsDbfs: rms > 0 ? toDbfs(rms) : null,
    clippedSamples: clipped,
    dcOffset: n > 0 ? sum / n : 0,
    silentRatio: silentWindowRatio(samples, sampleRate),
  }
}

export function concatChunks(chunks: readonly Float32Array[]): Float32Array {
  const total = chunks.reduce((acc, c) => acc + c.length, 0)
  const out = new Float32Array(total)
  let offset = 0
  for (const c of chunks) {
    out.set(c, offset)
    offset += c.length
  }
  return out
}

function silentWindowRatio(samples: Float32Array, sampleRate: number): number {
  const size = Math.max(1, Math.round(sampleRate * WINDOW_SECONDS))
  let windows = 0
  let silent = 0
  for (let start = 0; start < samples.length; start += size) {
    const end = Math.min(samples.length, start + size)
    let sq = 0
    for (let i = start; i < end; i++) {
      const s = samples[i] ?? 0
      sq += s * s
    }
    const rms = Math.sqrt(sq / (end - start))
    windows++
    if (rms === 0 || toDbfs(rms) < SILENCE_DBFS) silent++
  }
  return windows > 0 ? silent / windows : 0
}

function toDbfs(rms: number): number {
  return 20 * Math.log10(rms)
}
